import { iterateEntries, count } from "../../../../../Common/IterableUtils";
import { ICustomValidationFunctionCallContextProvider } from "../../Form/CustomizationContexts/ValidatorContext/ICustomValidationFunctionCallContextProvider";
import { ICalculator2 } from "../Calculator2/Calculator2";
import { LegacyValidationFunctionAdapter } from "../Calculator2/Validations/LegacyValidationFunctionAdapter";
import { IErrorManager } from "../ErrorManager/ErrorManager";
import { Localization } from "../Localization/Localization";
import { IPicklistsProvider } from "../Picklists/IPicklistsProvider";
import { FormStore } from "../Store/CandyStore";
import { IEvaluationContext } from "../Calculator2/IEvaluationContext";
import { AbsoluteModelPath, createModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { AggregatedValidationFunction } from "../Calculator2/AggregatedValidationFunction";
import { getValueByPath } from "../../../Helpers/StateAccessors/GetValueByPath";
import { ModelAccessor, ModelCountAccessor } from "../../../Controls/withEvaluators";

import { ValidatorResources } from "./ValidatorResources";

export interface IValidator {
    disableAllValidations(): void;
    enableAllValidations(): void;

    loadValidationFunctions(validatorResources: ValidatorResources): void;
    getAggregatedValidationFunctionByPath(path: AbsoluteModelPath): undefined | AggregatedValidationFunction;
}

export class Validator implements IValidator {
    private readonly calculator2: ICalculator2;
    private readonly customValidationFunctionCallContextProvider: ICustomValidationFunctionCallContextProvider;
    private readonly localization: Localization;
    private readonly store: FormStore;
    private readonly errorManager: IErrorManager;
    private readonly picklistsProvider: IPicklistsProvider;

    private validationsDisabled = false;

    public constructor(
        customValidationFunctionCallContextProvider: ICustomValidationFunctionCallContextProvider,
        localization: Localization,
        store: FormStore,
        errorManager: IErrorManager,
        picklistsProvider: IPicklistsProvider,
        calculator2: ICalculator2,
        validationFunctions: ValidatorResources | undefined,
        enableLegacyValidators: boolean
    ) {
        this.customValidationFunctionCallContextProvider = customValidationFunctionCallContextProvider;
        this.localization = localization;
        this.store = store;
        this.errorManager = errorManager;
        this.picklistsProvider = picklistsProvider;
        this.calculator2 = calculator2;
        this.loadValidationFunctions(validationFunctions ?? {}, enableLegacyValidators);
    }

    public loadValidationFunctions(
        validatorResources: ValidatorResources,
        enableLegacyValidators: boolean = false
    ): void {
        for (const [validatorPathMask, validator] of iterateEntries(validatorResources)) {
            if (validatorPathMask === "") {
                // NOTE В approval тестах на Engine есть по всей видимости забагованные валидации
                continue;
            }
            if (Array.isArray(validator)) {
                for (const validatorItem of validator) {
                    const conditionContext = validatorItem[1];
                    const conditionContextAndDeps = validatorItem[0];
                    const conditionDeps = conditionContextAndDeps.map(x => x[2]).flat();
                    const condition = (evaluationContext: IEvaluationContext) =>
                        conditionContextAndDeps
                            .map(([generatedExpressionFn, contextCurrentElement]) => {
                                const getValueByPathGeneratedFn: ModelAccessor = (expressionPath, fieldName) => {
                                    if (expressionPath.startsWith("@")) {
                                        const state = this.store.getState();
                                        return getValueByPath(state, () => "", {}, expressionPath, fieldName);
                                    }

                                    const generatedAbsoluteModelPath = contextCurrentElement
                                        .joinWith(createModelPath(expressionPath, "auto"))
                                        .normalize();

                                    if (fieldName === "children") {
                                        const instances = evaluationContext.getInstances(generatedAbsoluteModelPath);
                                        return Array.isArray(instances) ? (instances as string[]) : [...instances];
                                    }

                                    return evaluationContext.get(generatedAbsoluteModelPath) ?? "";
                                };

                                const getCountByPathGeneratedFn: ModelCountAccessor = expressionPath => {
                                    const generatedAbsoluteModelPath = contextCurrentElement
                                        .joinWith(createModelPath(expressionPath, "auto"))
                                        .normalize();
                                    return count(evaluationContext.getInstances(generatedAbsoluteModelPath));
                                };

                                return generatedExpressionFn(getValueByPathGeneratedFn, getCountByPathGeneratedFn);
                            })
                            .reduce((x, y) => x && y, true);

                    this.calculator2.addCalculationFunction(
                        new LegacyValidationFunctionAdapter(
                            validatorPathMask,
                            conditionContext,
                            this.localization,
                            this.store,
                            this.errorManager,
                            this.picklistsProvider,
                            this.customValidationFunctionCallContextProvider,
                            enableLegacyValidators,
                            () => this.validationsDisabled,
                            conditionDeps,
                            condition
                        )
                    );
                }
            } else {
                this.calculator2.addCalculationFunction(
                    new LegacyValidationFunctionAdapter(
                        validatorPathMask,
                        validator,
                        this.localization,
                        this.store,
                        this.errorManager,
                        this.picklistsProvider,
                        this.customValidationFunctionCallContextProvider,
                        enableLegacyValidators,
                        () => this.validationsDisabled,
                        [],
                        undefined
                    )
                );
            }
        }
    }

    public getAggregatedValidationFunctionByPath(path: AbsoluteModelPath): undefined | AggregatedValidationFunction {
        const functions = this.calculator2
            .getFunctionsByTarget(path)
            .filter((x): x is AggregatedValidationFunction => x instanceof AggregatedValidationFunction);
        return functions[0];
    }

    public disableAllValidations(): void {
        this.validationsDisabled = true;
    }

    public enableAllValidations(): void {
        this.validationsDisabled = false;
    }
}
